'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Bell, Search, MapPin, Globe, X, ChevronDown } from 'lucide-react';

const LANGUAGES = [
  { code: 'en', label: 'English', flag: '🇬🇧', short: 'EN' },
  { code: 'sw', label: 'Kiswahili', flag: '🇰🇪', short: 'SW' },
  { code: 'zh', label: '中文', flag: '🇨🇳', short: 'ZH' },
  { code: 'fr', label: 'Français', flag: '🇫🇷', short: 'FR' },
  { code: 'ar', label: 'العربية', flag: '🇸🇦', short: 'AR' },
  { code: 'hi', label: 'हिन्दी', flag: '🇮🇳', short: 'HI' },
];

export default function HomeTopBar() {
  const [langOpen, setLangOpen] = useState(false);
  const [selectedLang, setSelectedLang] = useState(LANGUAGES?.[0]);

  return (
    <>
      <header className="sticky top-0 z-30 flex items-center justify-between px-0 py-4 gap-3"
        style={{ background: 'rgba(10,15,30,0.92)', backdropFilter: 'blur(20px)', borderBottom: '1px solid var(--border)' }}>
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl overflow-hidden flex-shrink-0 flex items-center justify-center"
            style={{ background: 'rgba(0,200,83,0.1)', border: '1px solid rgba(0,200,83,0.3)' }}>
            <Image src="/assets/china-ndogo-logo.svg" alt="China Ndogo Logo" width={32} height={32} priority />
          </div>
          <div>
            <span className="text-lg font-800 tracking-tight text-gradient-green">CHINA NDOGO</span>
            <div className="flex items-center gap-1">
              <span className="text-[9px] font-700 tracking-widest" style={{ color: '#FFB300' }}>MKUBWA WAO</span>
              <span className="text-[9px]" style={{ color: 'var(--border)' }}>·</span>
              <MapPin size={9} style={{ color: 'var(--muted-foreground)' }} />
              <span className="text-[9px] font-500" style={{ color: 'var(--muted-foreground)' }}>Nairobi</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Language selector */}
          <button
            onClick={() => setLangOpen(true)}
            className="flex items-center gap-1 px-2.5 py-2 rounded-xl transition-all duration-150"
            style={{ background: 'var(--secondary)', border: '1px solid var(--border)' }}>
            <span className="text-sm">{selectedLang?.flag}</span>
            <span className="text-xs font-600 hidden sm:block" style={{ color: 'var(--muted-foreground)' }}>{selectedLang?.short}</span>
            <ChevronDown size={11} style={{ color: 'var(--muted-foreground)' }} />
          </button>

          <Link href="/universal-search-screen"
            className="flex items-center gap-2 px-3 py-2 rounded-xl transition-all duration-150 hover:bg-muted"
            style={{ background: 'var(--secondary)', border: '1px solid var(--border)' }}>
            <Search size={15} style={{ color: 'var(--muted-foreground)' }} />
            <span className="text-xs font-500 hidden sm:block" style={{ color: 'var(--muted-foreground)' }}>Search anything...</span>
          </Link>
          <Link href="/notifications-screen" className="relative p-2.5 rounded-xl transition-all duration-150 hover:bg-muted active:scale-95"
            style={{ background: 'var(--secondary)', border: '1px solid var(--border)' }}>
            <Bell size={18} style={{ color: 'var(--muted-foreground)' }} />
            <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full" style={{ background: 'var(--danger)' }} />
          </Link>
        </div>
      </header>
      {/* Language Modal */}
      {langOpen && (
        <>
          <div className="fixed inset-0 z-50" style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(6px)' }}
            onClick={() => setLangOpen(false)} />
          <div className="fixed inset-x-4 top-1/2 -translate-y-1/2 z-50 rounded-2xl p-5 max-w-xs mx-auto"
            style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Globe size={18} style={{ color: 'var(--primary)' }} />
                <h3 className="text-base font-bold text-foreground">Language</h3>
              </div>
              <button onClick={() => setLangOpen(false)}
                className="w-8 h-8 rounded-xl flex items-center justify-center"
                style={{ background: 'var(--secondary)', border: '1px solid var(--border)' }}>
                <X size={14} style={{ color: 'var(--muted-foreground)' }} />
              </button>
            </div>
            <div className="flex flex-col gap-2">
              {LANGUAGES?.map((lang) => (
                <button
                  key={lang?.code}
                  onClick={() => { setSelectedLang(lang); setLangOpen(false); }}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-150 active:scale-95 text-left"
                  style={{
                    background: selectedLang?.code === lang?.code ? 'rgba(0,200,83,0.1)' : 'var(--secondary)',
                    border: `1px solid ${selectedLang?.code === lang?.code ? 'rgba(0,200,83,0.4)' : 'var(--border)'}`,
                  }}>
                  <span className="text-xl">{lang?.flag}</span>
                  <div className="flex-1">
                    <p className="text-sm font-600 text-foreground">{lang?.label}</p>
                    <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{lang?.short}</p>
                  </div>
                  {selectedLang?.code === lang?.code && (
                    <div className="w-5 h-5 rounded-full flex items-center justify-center"
                      style={{ background: 'var(--primary)' }}>
                      <span className="text-[10px] text-black font-bold">✓</span>
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </>
  );
}